import { Button, Flex, Form, Input, Modal, Typography } from 'antd'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { KeyOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons'
import { authClient, getAuthError } from '@connectors/AuthClient'
import { useNotifications } from '@store/notifications'

const OTP_LENGTH = 6

export const LoginForm: React.FC<{ onSuccess?: () => void }> = ({ onSuccess }) => {
    const { refetch } = authClient.useSession()
    const [form] = Form.useForm()
    const navigate = useNavigate()

    const [isMfaModalOpen, setIsMfaModalOpen] = useState<boolean>(false)
    const [verificationCode, setVerificationCode] = useState<string>('')
    const [useBackupCode, setUseBackupCode] = useState<boolean>(false)
    const [backupCode, setBackupCode] = useState<string>('')
    const [isMFASubmitting, setIsMFASubmitting] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const { showNotification } = useNotifications()

    const completeLogin = async () => {
        await refetch()
        navigate('/users')
        onSuccess?.()
    }

    const onFinish = async (values: { email: string; password: string }) => {
        try {
            setIsSubmitting(true)

            const data: unknown = await authClient.signIn.email(
                {
                    email: values.email,
                    password: values.password
                },
                { throw: true }
            )

            // Better Auth returns twoFactorRedirect instead of a session when 2FA is enabled
            if (data && typeof data === 'object' && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
                setVerificationCode('')
                setBackupCode('')
                setUseBackupCode(false)
                setIsMfaModalOpen(true)
                return
            }

            await completeLogin()
        } catch (err) {
            switch (getAuthError(err)?.code) {
                case 'INVALID_EMAIL':
                case 'INVALID_EMAIL_OR_PASSWORD':
                    showNotification({
                        title: 'Invalid email or password',
                        description: 'Please try again',
                        type: 'error'
                    })
                    break
                case 'TOO_MANY_REQUESTS':
                    showNotification({
                        title: 'Too many attempts',
                        description: 'Please wait a moment and try again',
                        type: 'error'
                    })
                    break
                default:
                    showNotification({
                        title: 'An error occurred while logging in',
                        description: 'Please try again',
                        type: 'error'
                    })
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const onSubmitMFA = async (code: string) => {
        try {
            setIsMFASubmitting(true)

            // Stores a cookie so the user doesn't have to enter a 2FA code on this device for subsequent logins
            if (useBackupCode) {
                await authClient.twoFactor.verifyBackupCode({ code: code.trim() }, { throw: true })
            } else {
                await authClient.twoFactor.verifyTotp({ code }, { throw: true })
            }

            setIsMfaModalOpen(false)
            await completeLogin()
        } catch (err) {
            setVerificationCode('')
            setBackupCode('')

            switch (getAuthError(err)?.code) {
                case 'INVALID_CODE':
                case 'INVALID_BACKUP_CODE':
                case 'OTP_HAS_EXPIRED':
                    showNotification({
                        title: useBackupCode ? 'Invalid backup code' : 'Invalid or expired OTP',
                        description: 'Please try again',
                        type: 'error'
                    })
                    break
                case 'INVALID_TWO_FACTOR_COOKIE':
                    // The pending 2FA session expired, so the user has to sign in again
                    setIsMfaModalOpen(false)
                    showNotification({
                        title: 'Your sign in session has expired',
                        description: 'Please sign in again',
                        type: 'error'
                    })
                    break
                case 'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE':
                case 'TOO_MANY_REQUESTS':
                    showNotification({
                        title: 'Too many attempts',
                        description: 'Please wait a moment and try again',
                        type: 'error'
                    })
                    break
                default:
                    showNotification({
                        title: 'An error occurred while submitting MFA',
                        description: 'Please try again',
                        type: 'error'
                    })
            }
        } finally {
            setIsMFASubmitting(false)
        }
    }

    return (
        <>
            <Form form={form} name="login" layout="vertical" size="large" requiredMark={false} onFinish={onFinish}>
                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' }
                    ]}
                >
                    <Input prefix={<MailOutlined />} placeholder="you@example.com" autoComplete="email" autoFocus />
                </Form.Item>
                <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: true, message: 'Please enter your password' }]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" block loading={isSubmitting}>
                        Sign in
                    </Button>
                </Form.Item>
            </Form>
            <Modal
                open={isMfaModalOpen}
                onCancel={() => setIsMfaModalOpen(false)}
                footer={null}
                width={420}
                centered
                destroyOnHidden
            >
                <Flex gap="large" vertical align="center" style={{ paddingTop: 8 }}>
                    <SafetyOutlined style={{ fontSize: 40 }} />
                    <Flex gap="xsmall" vertical align="center">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Two-factor authentication
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {useBackupCode
                                ? 'Enter one of the backup codes you saved when enabling MFA'
                                : 'Enter the 6-digit code from your authenticator app'}
                        </Typography.Text>
                    </Flex>
                    {useBackupCode ? (
                        <Input
                            prefix={<KeyOutlined />}
                            size="large"
                            placeholder="Backup code"
                            value={backupCode}
                            autoFocus
                            autoComplete="one-time-code"
                            disabled={isMFASubmitting}
                            onChange={e => setBackupCode(e.target.value)}
                            onPressEnter={() => backupCode.trim() && onSubmitMFA(backupCode)}
                        />
                    ) : (
                        <Input.OTP
                            length={OTP_LENGTH}
                            size="large"
                            value={verificationCode}
                            autoFocus
                            disabled={isMFASubmitting}
                            onChange={code => {
                                setVerificationCode(code)
                                onSubmitMFA(code)
                            }}
                            onInput={values => setVerificationCode(values.join(''))}
                        />
                    )}
                    <Button
                        type="primary"
                        block
                        size="large"
                        onClick={() => onSubmitMFA(useBackupCode ? backupCode : verificationCode)}
                        loading={isMFASubmitting}
                        disabled={useBackupCode ? !backupCode.trim() : verificationCode.length !== OTP_LENGTH}
                    >
                        Verify
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        disabled={isMFASubmitting}
                        onClick={() => {
                            setVerificationCode('')
                            setBackupCode('')
                            setUseBackupCode(!useBackupCode)
                        }}
                    >
                        {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
                    </Button>
                </Flex>
            </Modal>
        </>
    )
}
